import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FaHandHoldingUsd } from 'react-icons/fa';
import { updateProjectBudget } from 'redux/booking/travel/travelBookingSlice';
import { Modal, Button, Alert } from 'components/common';

import { INPUT_CLS as inputClass } from 'helpers/formStyles';
const labelClass = 'tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1.5';
const errClass = 'tw-text-xs tw-text-red-600 tw-mt-1 tw-mb-0';

const BookingUpdateBudgetModal = ({ showModal, setShowModal, booking }) => {
  const { register, handleSubmit, formState: { errors }, setValue, getValues } = useForm();
  const { usdExchangeRateData } = useSelector((state) => state.settings);
  const dispatch = useDispatch();

  useEffect(() => {
    if (booking?.project) setValue('budget_amount_currency', booking.project.budget_amount_currency);
    setValue('budget_exchange_rate', usdExchangeRateData);
  }, [setValue, booking, usdExchangeRateData]);

  const closeModal = () => setShowModal(false);

  const onSubmit = async (data) => {
    try {
      await dispatch(updateProjectBudget({ bookingId: booking.id, data })).unwrap();
      toast.success('Project budget updated.');
      closeModal();
    } catch (error) {
      toast.error(error?.error || error?.message || 'Failed to update project budget.');
    }
  };

  return (
    <Modal
      show={showModal}
      onHide={closeModal}
      title="Update project budget for trip"
      size="lg"
      footer={(
        <>
          <Button size="sm" variant="secondary" onClick={closeModal}>Cancel</Button>
          <Button size="sm" type="submit" form="update-budget-form" variant="primary" leftIcon={FaHandHoldingUsd}>Update</Button>
        </>
      )}
    >
      <form id="update-budget-form" onSubmit={handleSubmit(onSubmit)}>
        <Alert type="info" className="tw-mb-4">
          In the case of a trip following the project&apos;s job, there must be a budget based on the price matrix. The information is confirmed by the accounting department.
        </Alert>

        <div className="tw-mb-4">
          {/* This field is the budget AMOUNT in USD (`budget_amount_currency`),
              not a currency code — the old "Budget currency" label read as one. */}
          <label className={labelClass}>Budget amount (USD)</label>
          <input
            placeholder="Budget amount in USD"
            className={inputClass}
            {...register('budget_amount_currency', { required: 'Budget amount must be required' })}
            onInput={(event) => {
              const exchangeRate = parseFloat(getValues('budget_exchange_rate')) || 1;
              setValue('budget_amount', parseFloat(event.target.value) * exchangeRate || 0);
            }}
          />
          {errors.budget_amount_currency && <p className={errClass}>{errors.budget_amount_currency.message}</p>}
        </div>

        <div className="tw-mb-4">
          <label className={labelClass}>Exchange rate</label>
          <input placeholder="Exchange rate" className={inputClass}
            {...register('budget_exchange_rate', { required: 'Exchange rate must be required' })} />
          {errors.budget_exchange_rate && <p className={errClass}>{errors.budget_exchange_rate.message}</p>}
          <Alert type="info" className="tw-mt-2">
            USD exchange rate to VND from <a href="https://www.vietcombank.com.vn/KHCN/Cong-cu-tien-ich/Ty-gia?devicechannel=default" target="_blank" rel="noreferrer" className="tw-underline">Vietcombank</a>.
          </Alert>
        </div>

        <div className="tw-mb-4">
          <label className={labelClass}>Budget amount (VND)</label>
          <input placeholder="Budget amount in VND" className={inputClass} disabled
            {...register('budget_amount', { required: 'Budget amount must be required' })} />
          <div className="tw-mt-1 tw-text-xs tw-text-slate-500">Auto-calculated: budget amount (USD) × exchange rate.</div>
        </div>

        <div>
          <label className={labelClass}>Note</label>
          <textarea rows={6} placeholder="Note" className={inputClass} {...register('budget_notes')} />
        </div>
      </form>
    </Modal>
  );
};

BookingUpdateBudgetModal.propTypes = {
  showModal: PropTypes.bool,
  setShowModal: PropTypes.func,
  booking: PropTypes.object,
};

export default BookingUpdateBudgetModal;
